import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, BookOpen, Bell, MessageSquare } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet-async';
import DOMPurify from 'dompurify';
import ShareButtons from '../components/ShareButtons';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABELS = { editorial: '📰 Éditorial', invite: '👤 Invité', etude: '📊 Étude & Chiffres', prolongement: '🔗 Cours lié' };

function timeAgo(d) {
  const diff = Date.now() - new Date(d).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return "Aujourd'hui";
  if (days === 1) return "Hier";
  return `Il y a ${days} jours`;
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterDone, setNewsletterDone] = useState(false);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const r = await axios.get(`${API}/api/articles/${slug}`, { withCredentials: true });
        setArticle(r.data);
      } catch { navigate('/articles'); }
      finally { setLoading(false); }
    };
    fetchArticle();
  }, [slug]);

  const handleComment = async (e) => {
    e.preventDefault();
    if (!user) { toast.error('Connectez-vous pour commenter'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/articles/${article.article_id}/comments`,
        { content: comment }, { withCredentials: true });
      toast.success('Commentaire publié !');
      setComment('');
      const r = await axios.get(`${API}/api/articles/${slug}`, { withCredentials: true });
      setArticle(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setSubmitting(false); }
  };

  const handleNewsletter = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/api/articles/newsletter/subscribe`, { email: newsletterEmail, name: user?.name || '' });
      setNewsletterDone(true);
      toast.success('Abonné(e) avec succès !');
    } catch { toast.error('Erreur d\'abonnement'); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0f0e17] flex items-center justify-center">
      <div className="w-8 h-8 rounded-full border-2 border-[#ff6b6b] border-t-transparent animate-spin" />
    </div>
  );

  if (!article) return null;

  const fullUrl = `${window.location.origin}/articles/${slug}`;

  return (
    <>
      <Helmet>
        <title>{article.title} — Kurso</title>
        <meta name="description" content={article.meta_description || article.title} />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.meta_description || ''} />
        {article.cover_image && <meta property="og:image" content={article.cover_image} />}
        <meta property="og:url" content={fullUrl} />
        <meta property="og:type" content="article" />
        <link rel="alternate" hrefLang="fr" href={fullUrl} />
        <link rel="alternate" hrefLang="en" href={fullUrl + '?lang=en'} />
      </Helmet>

      <div className="min-h-screen bg-[#0f0e17]">
        <header className="sticky top-0 z-50 bg-[#0f0e17]/90 backdrop-blur-xl border-b border-white/5 px-4 py-3">
          <div className="max-w-2xl mx-auto flex items-center justify-between">
            <button onClick={() => navigate('/articles')} className="flex items-center gap-2 text-[#e8e8e8] hover:text-white transition-colors">
              <ArrowLeft size={18} /><span className="text-sm">Articles</span>
            </button>
            <ShareButtons url={fullUrl} message={article.title} compact networks={['linkedin', 'facebook', 'whatsapp', 'twitter']} />
          </div>
        </header>

        <article className="max-w-2xl mx-auto px-4 py-8">
          {/* Cover image */}
          {article.cover_image && (
            <div className="rounded-2xl overflow-hidden mb-6 h-64">
              <img src={article.cover_image} alt={article.title} className="w-full h-full object-cover" />
            </div>
          )}

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="text-xs bg-[#ff6b6b]/15 text-[#ff6b6b] px-2 py-0.5 rounded-full">{article.category}</span>
            {article.article_type && <span className="text-xs text-[#a0a0ab]">{TYPE_LABELS[article.article_type]}</span>}
            <span className="flex items-center gap-1 text-xs text-[#a0a0ab] ml-auto">
              <Clock size={10} /> {article.estimated_reading_time} min
            </span>
          </div>

          <h1 className="text-3xl font-heading font-bold text-white mb-4">{article.title}</h1>
          {article.meta_description && (
            <p className="text-[#a0a0ab] text-base mb-6 leading-relaxed">{article.meta_description}</p>
          )}

          {/* Author card */}
          <div className="flex items-center gap-3 bg-white/5 rounded-2xl p-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-[#ff6b6b]/20 flex items-center justify-center text-[#ff6b6b] font-bold">
              {article.author_name?.[0]?.toUpperCase()}
            </div>
            <div>
              <p className="text-white text-sm font-medium">{article.author_name}</p>
              {article.author_bio && <p className="text-[#a0a0ab] text-xs">{article.author_bio}</p>}
            </div>
          </div>

          {/* Content */}
          <div
            className="prose prose-invert prose-sm max-w-none text-[#e8e8e8] leading-relaxed mb-8"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content, { USE_PROFILES: { html: true } }) }}
          />

          {/* Related course CTA */}
          {article.linked_course_id && (
            <div className="bg-[#ff6b6b]/5 border border-[#ff6b6b]/20 rounded-2xl p-4 mb-6">
              <p className="text-sm font-medium text-white mb-2">📚 Approfondir ce sujet</p>
              <p className="text-xs text-[#a0a0ab] mb-3">Découvrez notre parcours complet sur ce thème</p>
              <button onClick={() => navigate(`/course/${article.linked_course_id}`)}
                className="btn-primary text-sm py-2 px-4">
                Voir le cours
              </button>
            </div>
          )}

          {/* Share */}
          <div className="flex items-center gap-3 mb-8 p-4 bg-white/5 rounded-2xl">
            <p className="text-sm text-[#e8e8e8] flex-1">Partagez cet article</p>
            <ShareButtons url={fullUrl} message={article.title} networks={['linkedin', 'facebook', 'whatsapp', 'twitter']} label="Partager" />
          </div>

          {/* Newsletter opt-in */}
          {!newsletterDone ? (
            <div className="bg-gradient-to-r from-[#ff6b6b]/10 to-[#ffd93d]/10 border border-white/10 rounded-2xl p-5 mb-8">
              <div className="flex items-center gap-2 mb-2">
                <Bell size={16} className="text-[#ffd93d]" />
                <p className="text-white text-sm font-semibold">Recevoir les prochains articles</p>
              </div>
              <p className="text-xs text-[#a0a0ab] mb-3">Abonnez-vous à la newsletter Kurso pour ne manquer aucun article.</p>
              <form onSubmit={handleNewsletter} className="flex gap-2">
                <input
                  type="email"
                  required
                  value={newsletterEmail}
                  onChange={e => setNewsletterEmail(e.target.value)}
                  placeholder="votre@email.fr"
                  defaultValue={user?.email || ''}
                  className="flex-1 bg-[#0f0e17] border border-white/10 text-white placeholder-[#a0a0ab] rounded-xl px-3 py-2 text-xs focus:outline-none focus:border-[#ff6b6b]/50"
                />
                <button type="submit" className="btn-primary text-xs py-2 px-3">S'abonner</button>
              </form>
            </div>
          ) : (
            <div className="bg-[#06d6a0]/10 border border-[#06d6a0]/20 rounded-2xl p-4 mb-8 text-center text-sm text-[#06d6a0]">
              ✓ Vous êtes abonné(e) à la newsletter Kurso
            </div>
          )}

          {/* Comments */}
          <div className="mb-8">
            <h2 className="text-lg font-heading font-bold text-white mb-4 flex items-center gap-2">
              <MessageSquare size={18} className="text-[#ff6b6b]" />
              Commentaires ({article.comments?.length || 0})
            </h2>
            {user ? (
              <form onSubmit={handleComment} className="mb-4">
                <textarea
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                  placeholder="Votre commentaire..."
                  rows={3}
                  minLength={5}
                  maxLength={1000}
                  className="w-full bg-white/5 border border-white/10 text-white placeholder-[#a0a0ab] rounded-2xl px-4 py-3 text-sm resize-none focus:outline-none focus:border-[#ff6b6b]/50 mb-2"
                />
                <button type="submit" disabled={submitting || comment.length < 5} className="btn-primary text-sm py-2 px-4 disabled:opacity-50">
                  {submitting ? '...' : 'Commenter'}
                </button>
              </form>
            ) : (
              <div className="bg-white/5 rounded-2xl p-4 mb-4 text-center">
                <p className="text-sm text-[#a0a0ab]">
                  <a href="/auth" className="text-[#ff6b6b] hover:underline">Connectez-vous</a> pour laisser un commentaire
                </p>
              </div>
            )}
            <div className="space-y-3">
              {(article.comments || []).map(c => (
                <div key={c.comment_id} className="bg-white/5 rounded-xl p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-6 h-6 rounded-full bg-[#ff6b6b]/20 flex items-center justify-center text-[#ff6b6b] text-xs font-bold">
                      {c.author_name?.[0]?.toUpperCase()}
                    </div>
                    <span className="text-white text-xs font-medium">{c.author_name}</span>
                    <span className="text-[#a0a0ab] text-xs ml-auto">{timeAgo(c.created_at)}</span>
                  </div>
                  <p className="text-[#e8e8e8] text-xs pl-8">{c.content}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Related articles */}
          {article.related?.length > 0 && (
            <div className="mb-8">
              <h2 className="text-base font-heading font-bold text-white mb-3">Articles similaires</h2>
              <div className="grid gap-3">
                {article.related.map(r => (
                  <button key={r.article_id} onClick={() => navigate(`/articles/${r.slug}`)}
                    className="text-left bg-white/5 hover:bg-white/8 border border-white/10 rounded-xl p-3 transition-colors">
                    <p className="text-white text-sm font-medium line-clamp-2">{r.title}</p>
                    <p className="text-xs text-[#a0a0ab] mt-1">{r.author_name} · {r.estimated_reading_time} min</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          <Footer />
        </article>
      </div>
    </>
  );
}
